import { FunctionComponent, useState, useEffect, useMemo, useRef } from 'react';
import { Content } from './Content';
import { Footer } from './Footer';
import { parseHtml } from './Helper';
import SurveyType, { SurveyTypeValue } from '../Data/SurveyType';
import Service from '../Utilities/Service';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { SurveyHeader } from '../NewSurvey/SurveyHeader';
import { LoadingScreen } from '../components/LoadingScreen';
import handleServiceError, { isResponseLimitReached } from '../Utilities/handleServiceError';
import { PopupEventTypes } from '../Data/PopupEventTypes';
import useSignals from '../Utilities/useSignals';
import { useSafeLocalStorage } from '../Utilities/useSafeLocalStorage';
import type { StatusValue } from '../Data/Status';
import type { LegacyResponse, Respondent, Template } from '../types';

interface OldSurveyComponentProps {
  campaign: string | null;
  campaignMemberPublicGuid: string | null;
  shouldUseLocalStorage: boolean;
  unsubscribedInitially: boolean;
  responseLimitReachedInitially: boolean;
  enablePopup: boolean;
  instanceId: string | null;
  hideFooter: boolean;
  hideHeader: boolean;
  rating?: number | string;
  respondent: Respondent;
  allowEditResponse: boolean;
  template: Template;
  surveyType: SurveyTypeValue;
  setStatus: (n: StatusValue) => void;
  setErrorDetails: (s: string) => void;
}

interface PopupData {
  campaignMember?: Record<string, unknown>;
  [key: string]: unknown;
}

interface MultipleChoiceAnswer {
  answer: string[];
  question: string;
}

const getBackgroundColor = () => globalThis.getComputedStyle(document.body).backgroundColor;

const service = new Service().proxy;

export const OldSurveyComponent: FunctionComponent<OldSurveyComponentProps> = ({
  campaign,
  campaignMemberPublicGuid,
  shouldUseLocalStorage,
  unsubscribedInitially,
  responseLimitReachedInitially,
  enablePopup,
  instanceId,
  hideFooter,
  hideHeader,
  rating,
  respondent,
  allowEditResponse,
  template,
  surveyType,
  setStatus,
  setErrorDetails
}) => {
  const [stylingReady, setStylingReady] = useState(false);
  const [responseLocalStorage, setResponseLocalStorage] = useSafeLocalStorage<LegacyResponse>(
    `nps-survey-${campaignMemberPublicGuid ?? campaign}`,
    {},
    shouldUseLocalStorage
  );
  const [sending, setSending] = useState(false);
  const [newSection, setNewSection] = useState(false);
  const [notRated, setNotRated] = useState(true);
  const [questionFirst, setQuestionFirst] = useState(false);
  const [finished, setFinished] = useState(false);
  const [unsubscribed] = useState(unsubscribedInitially);
  const [responseLimitReached, setResponseLimitReached] = useState(responseLimitReachedInitially);
  const [allowRatingChange, setAllowRatingChange] = useState(false);
  const [allowPreviousButton, setAllowPreviousButton] = useState(false);
  const [showCategoryRequiredMessage, setShowCategoryRequiredMessage] = useState(false);
  const [isRatingPageRendered, setIsRatingPageRendered] = useState(false);
  const [popupData, setpopupData] = useState<PopupData>({});
  const appInsights = useAppInsightsContext();

  //used for universal link campaigns, after posting the response. If needing to edit it, this ref should hold the new id
  const newAnonymousPublicGuid = useRef('');

  const { signalSurveyCompleted, signalReady, signalSurveyPageChanged } = useSignals({
    enablePopup,
    instanceId,
    getBackgroundColor,
    isLegacySurvey: true
  });

  const showFooter = useMemo(() => {
    return !(enablePopup || hideFooter || unsubscribed);
  }, [enablePopup, hideFooter, unsubscribed]);

  const showHeader = useMemo(() => {
    return !enablePopup && !hideHeader;
  }, [enablePopup, hideHeader]);

  const isResponseLocalStorageEmpty = () => {
    return responseLocalStorage && Object.keys(responseLocalStorage).length === 0;
  };

  useEffect(() => {
    if (!stylingReady || !enablePopup || responseLimitReachedInitially) {
      return;
    }
    const receiveData = (event: MessageEvent) => {
      if (
        event.data.eventType === PopupEventTypes.SET_SURVEY_PARAMETERS &&
        event.data.instanceId === instanceId
      ) {
        setpopupData(event.data);
      }
    };
    globalThis.addEventListener('message', receiveData, false);

    signalReady();
    return () => {
      globalThis.removeEventListener('message', receiveData);
    };
  }, [instanceId, enablePopup, stylingReady, signalReady, responseLimitReachedInitially]);

  useEffect(() => {
    const isRated = typeof rating === 'number' || (rating?.length ?? 0) > 0;

    async function fixFlickering() {
      await import('../Dependencies/bootstrap-3.3.4-dist/css/bootstrap.min.css');
      await import('./index.css');
      setStylingReady(true); //prevents flickering at start of survey
    }
    fixFlickering();
    appInsights.trackPageView({ name: 'OldSurveyComponent.tsx' });

    const syncExistingResponse = () => {
      const response = respondent?.response;
      if (!response) return;

      const copy: LegacyResponse = isResponseLocalStorageEmpty() ? response : responseLocalStorage;

      if (response.closed !== true) {
        copy.closed = false;
      }

      if (isRated && allowEditResponse && !template.forceSingleSubmit) {
        copy.rating = Number.parseInt(rating as string);
        service.editResponse(copy).catch(handleResponseError);
        appInsights.trackEvent({ name: 'Rated', properties: copy });
      }
      setResponseLocalStorage({ ...copy, rating: rating ?? copy.rating });
    };

    if (isRated) {
      setNotRated(false);
    }
    syncExistingResponse();
    if (template.questionFirst) {
      setQuestionFirst(true);
    }
    if (template.allowRatingChange) {
      setAllowRatingChange(true);
    }
    if (surveyType === SurveyType.UNIVERSAL && isResponseLocalStorageEmpty()) {
      setResponseLocalStorage({
        email: '',
        firstName: 'Anonymous',
        lastName: null,
        rating: rating ?? null,
        comment: null,
        answer1: null,
        answer2: null,
        answer3: null,
        answer4: null,
        answer5: null,
        category: null,
        device: navigator.userAgent,
        allowedForPublicUse: responseLocalStorage.allowedForPublicUse ?? false
      });
    }
    if (template.consentForPublicUse && template.consentIsOptOut) {
      setResponseLocalStorage((prevResponse) => {
        return {
          ...prevResponse,
          allowedForPublicUse: true
        };
      });
    }

    const primaryColor = template?.colorScheme;
    if (primaryColor) {
      (document.querySelector(':root') as HTMLElement | null)?.style.setProperty(
        '--primary-color',
        primaryColor
      );
    }
  }, [
    rating,
    respondent,
    allowEditResponse,
    template,
    surveyType,
    setStatus,
    setErrorDetails,
    appInsights
  ]);

  async function toggleNextSection() {
    setSending(true);
    const hasRatedAndCategoryIsRequired =
      !notRated &&
      !template.hideCategories &&
      template.mandatoryCategoryQuestion &&
      !responseLocalStorage.category;
    if (hasRatedAndCategoryIsRequired) {
      setShowCategoryRequiredMessage(true);
      setSending(false);
      return;
    }
    try {
      if (surveyType !== SurveyType.UNIVERSAL) {
        const closedInformation = await service.responseClosed();
        const disAllowRatingChange = closedInformation?.closed;
        if (disAllowRatingChange) {
          // NOTE: preserves the original (latent) behavior of passing an object as the key arg.
          updateResponse(
            {
              ...responseLocalStorage,
              rating: closedInformation.previousRating,
              closed: true
            } as unknown as string,
            undefined
          );
        }
        if (!template.forceSingleSubmit) {
          await service.editResponse(responseLocalStorage);
        }
      }
      toggleNext();
    } catch (e) {
      handleResponseError(e);
      appInsights.trackException({ exception: e as Error, severityLevel: 3 });
    } finally {
      setSending(false);
    }
  }

  function toggleNext() {
    setNewSection(true);
    setAllowRatingChange(false);
    if (allowRatingChange) {
      setAllowPreviousButton(true);
    }
    setAllowRatingChange(false);

    if (enablePopup) {
      signalSurveyPageChanged();
    }
  }

  function togglePreviousSection() {
    setAllowRatingChange(true);
    setNewSection(false);
  }

  async function sendFinalResponse(onSuccess?: () => void) {
    setSending(true);
    if (
      !template.hideCategories &&
      template.mandatoryCategoryQuestion &&
      !responseLocalStorage.category
    ) {
      setShowCategoryRequiredMessage(true);
      setSending(false);
      return;
    }
    if (enablePopup) {
      try {
        const surveyResponse = {
          ...responseLocalStorage,
          userComment: responseLocalStorage.comment
        };
        await service.addPopupResponse(surveyResponse, popupData.campaignMember);
        setFinished(true);
        setAllowRatingChange(false);
        setNewSection(false);
        onSuccess?.();
        signalSurveyCompleted();
      } catch (e) {
        handleResponseError(e);
      } finally {
        setSending(false);
      }
      return;
    }

    try {
      if (surveyType === SurveyType.UNIVERSAL) {
        if (newAnonymousPublicGuid.current) {
          await service.updatePublicConsentOnAnonymousCampaignMember(
            responseLocalStorage,
            newAnonymousPublicGuid.current
          );
        } else {
          newAnonymousPublicGuid.current = await service.addUniversalLinkResponse(
            responseLocalStorage
          );
        }
      } else await service.addFinalResponse(responseLocalStorage);
      setFinished(true);
      setAllowRatingChange(false);
      setNewSection(false);
      onSuccess?.();
      signalSurveyCompleted();
    } catch (e) {
      handleResponseError(e);
    } finally {
      setSending(false);
    }
  }

  function handleResponseError(e: unknown) {
    if (isResponseLimitReached(e)) {
      setResponseLimitReached(true);
      return;
    }
    handleServiceError(e, setStatus, setErrorDetails);
  }

  async function rate(ratingValue: number) {
    const updatedResponse = { ...responseLocalStorage, rating: ratingValue };
    setResponseLocalStorage(updatedResponse);
    if (surveyType !== SurveyType.UNIVERSAL && !template.forceSingleSubmit) {
      setSending(true);
      await service
        .editResponse(updatedResponse)
        .then(() => {
          setNotRated(false);
        })
        .finally(() => {
          setSending(false);
        })
        .catch(handleResponseError);
    } else {
      setNotRated(false);
    }
  }

  function updateResponse(key: string, value: unknown) {
    setResponseLocalStorage({ ...responseLocalStorage, [key]: value });
  }

  function setMultipleChoice(
    e: React.MouseEvent<HTMLInputElement>,
    answerKey: string,
    choice: string
  ) {
    const questionKey = 'question' + answerKey.slice(-1);
    const question = JSON.parse(template[questionKey] as string);
    //if radio
    if (!question.allowMultipleAnswers) {
      setResponseLocalStorage({
        ...responseLocalStorage,
        [answerKey]: JSON.stringify({
          answer: [choice],
          question: question.question
        })
      });
      return;
    }
    //if checkbox
    const checked = (e.target as HTMLInputElement).checked;
    const copy: MultipleChoiceAnswer | null = JSON.parse(responseLocalStorage[answerKey] as string);
    //if no previous answer, create the first one
    if (copy === null) {
      setResponseLocalStorage({
        ...responseLocalStorage,
        [answerKey]: JSON.stringify({
          answer: [choice],
          question: question.question
        })
      });
      return;
    }
    //if checked add to answer list
    if (checked) copy.answer.push(choice);
    //else remove from answer list
    else copy.answer = copy.answer.filter((a) => a !== choice);
    //update state with the edited copy
    setResponseLocalStorage({
      ...responseLocalStorage,
      [answerKey]: JSON.stringify(copy)
    });
  }

  return (
    <>
      {(!stylingReady || !template) && <LoadingScreen />}
      {template && stylingReady && (
        <div
          className="comment-wrapper comment-wrapper-desktop comment-wrapper-mobile"
          style={{
            fontFamily: template.fontFamily
              ? `${template.fontFamily}, Roboto, Arial, sans-serif`
              : 'Roboto, Arial, sans-serif'
          }}
        >
          {showHeader && <SurveyHeader template={template} />}
          {isRatingPageRendered && surveyType === SurveyType.UNIVERSAL && template.introText && (
            <div className="intro-text">{parseHtml(template.introText)}</div>
          )}
          <Content
            notRated={notRated}
            allowPreviousButton={allowPreviousButton}
            allowRatingChange={allowRatingChange}
            sending={sending}
            finished={finished}
            newSection={newSection}
            questionFirst={questionFirst}
            unsubscribed={unsubscribed}
            responseLimitReached={responseLimitReached}
            template={template}
            response={responseLocalStorage}
            respondent={respondent}
            rate={rate}
            surveyType={surveyType}
            toggleNextSection={toggleNextSection}
            togglePreviousSection={togglePreviousSection}
            updateResponse={updateResponse}
            setMultipleChoice={setMultipleChoice}
            sendFinalResponse={sendFinalResponse}
            showCategoryRequiredMessage={showCategoryRequiredMessage}
            setIsRatingPageRendered={setIsRatingPageRendered}
          />
          {showFooter && (
            <Footer template={template} surveyType={surveyType} respondent={respondent} />
          )}
        </div>
      )}
    </>
  );
};
